export function focusElement(element, delay = 0): Promise<void> {
  return new Promise((resolve) => {
    // We need to put the `element.focus()` call on top of the stack to make sure IE executes the focus after everything else. This can be done with `setTimeout`.
    setTimeout(() => {
      element.focus();
      resolve();
    }, delay);
  });
}

export function getFocusableElementList() {
  return 'a[href], area[href], input:not([disabled]), input:not([hidden]), select:not([disabled]), select:not([hidden]), textarea:not([disabled]), textarea:not([hidden]), button:not([disabled]), [tabindex="0"]';
}

export function find(items, fn) {
  // Use default find if supported
  if (Array.prototype.find) {
    return items.find(fn);
  }
  // `find` method is not defined on `Array`, so we implement it
  let i = 0;
  let found = null;
  while (!found && i < items.length) {
    found = fn(items[i]) ? items[i] : null;
    i += 1;
  }
  return found;
}

// Use this function to generate an ID.
// For example, this will ensure that each 'som-radio-button' components will have a unique ID automatically.
let lastId = 0;
export function generateId(prefix: string = 'id') {
  lastId += 1;
  return `${prefix}${lastId}`;
}

export function whichTransitionEvent() {
  const el = document.createElement('fakeelement');

  const transitions = {
    transition: 'transitionend',
    OTransition: 'oTransitionEnd',
    MozTransition: 'transitionend',
    WebkitTransition: 'webkitTransitionEnd',
  };

  const transitionKeys = Object.keys(transitions);
  let found = false;
  let i = 0;
  while (!found && i < transitionKeys.length) {
    const t = transitionKeys[i];
    if (el.style[t] !== undefined) {
      found = true;
    } else {
      i += 1;
    }
  }

  if (found) {
    const t = transitionKeys[i];
    return transitions[t];
  }

  return null;
}

export function whichAnimationEvent() {
  const el = document.createElement('fakeelement');

  const animations = {
    animation: 'animationend',
    OAnimation: 'oAnimationEnd',
    MozAnimation: 'animationend',
    WebkitAnimation: 'webkitAnimationEnd',
  };

  const animationKeys = Object.keys(animations);
  let found = false;
  let i = 0;
  while (!found && i < animationKeys.length) {
    const t = animationKeys[i];
    if (el.style[t] !== undefined) {
      found = true;
    } else {
      i += 1;
    }
  }

  if (found) {
    const t = animationKeys[i];
    return animations[t];
  }

  return null;
}

export function waitForCSSTransitionToFinish(elem: HTMLElement, callback: Function) {
  // Listen for a transition animation animation
  const transitionEvent = whichTransitionEvent();
  let onTransitionCompletedCb;
  const onTransitionCompleted = (evt) => {
    // Make sure to wait for the proper transition event
    if (evt.target === elem) {
      elem.removeEventListener(transitionEvent, onTransitionCompletedCb);
      callback();
    }
  };
  onTransitionCompletedCb = onTransitionCompleted.bind(this);
  if (transitionEvent) {
    elem.addEventListener(transitionEvent, onTransitionCompletedCb);
  }
}

export function runAfterNextFrameRender(callback: Function) {
  requestAnimationFrame(() => {
    setTimeout(() => {
      callback();
    }, 0);
  });
}

// Returns true of an element is a child of another element; false otherwise
export function isDescendant(parent, child) {
  let node = child.parentNode;
  while (node !== null) {
    if (node === parent) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
}

/**
 * 
 * @returns Returns JSX 
 */
export function getRequiredInd(h, required, requiredInd) {
  let _requiredInd;

  if (required && requiredInd === 'required') {
    _requiredInd = <span className="required-indicator" aria-hidden="true"> *</span>
  } else if ((!required && !requiredInd) || (!required && requiredInd === 'optional')) {
    _requiredInd = <span className="text-optional" aria-hidden="true">(optional)</span>
  }
  return _requiredInd;
}

export function isInError(error) {
  if (typeof error !== 'string') return false;

  return (error !== null && error.length > 0);
}

export type Attributes = { [key: string]: any };

/**
 * Elements inside of web components sometimes need to inherit global attributes
 * set on the host. For example, the inner input in `ion-input` should inherit
 * the `title` attribute that developers set directly on `ion-input`. This
 * helper function should be called in componentWillLoad and assigned to a variable
 * that is later used in the render function.
 *
 * This does not need to be reactive as changing attributes on the host element
 * does not trigger a re-render.
 */
export const inheritAttributes = (el: HTMLElement, attributes: string[] = []) => {
  const attributeObject: Attributes = {};

  attributes.forEach((attr) => {
    if (el.hasAttribute(attr)) {
      const value = el.getAttribute(attr);
      if (value !== null) {
        attributeObject[attr] = el.getAttribute(attr);
      }
      el.removeAttribute(attr);
    }
  });

  return attributeObject;
};

/**
 * List of available ARIA attributes + `role`.
 * Removed deprecated attributes.
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes
 */
const ariaAttributes = [
  'role',
  'aria-activedescendant',
  'aria-atomic',
  'aria-autocomplete',
  'aria-braillelabel',
  'aria-brailleroledescription',
  'aria-busy',
  'aria-checked',
  'aria-colcount',
  'aria-colindex',
  'aria-colindextext',
  'aria-colspan',
  'aria-controls',
  'aria-current',
  'aria-describedby',
  'aria-description',
  'aria-details',
  'aria-disabled',
  'aria-errormessage',
  'aria-expanded',
  'aria-flowto',
  'aria-haspopup',
  'aria-hidden',
  'aria-invalid',
  'aria-keyshortcuts',
  'aria-label',
  'aria-labelledby',
  'aria-level',
  'aria-live',
  'aria-multiline',
  'aria-multiselectable',
  'aria-orientation',
  'aria-owns',
  'aria-placeholder',
  'aria-posinset',
  'aria-pressed',
  'aria-readonly',
  'aria-relevant',
  'aria-required',
  'aria-roledescription',
  'aria-rowcount',
  'aria-rowindex',
  'aria-rowindextext',
  'aria-rowspan',
  'aria-selected',
  'aria-setsize',
  'aria-sort',
  'aria-valuemax',
  'aria-valuemin',
  'aria-valuenow',
  'aria-valuetext',
];

/**
 * Returns an array of aria attributes that should be copied from
 * the shadow host element to a target within the light DOM.
 * @param el The element that the attributes should be copied from.
 * @param ignoreList The list of aria-attributes to ignore reflecting and removing from the host.
 * Use this in instances where we manually specify aria attributes on the `<Host>` element.
 */
export const inheritAriaAttributes = (el: HTMLElement, ignoreList?: string[]) => {
  let attributesToInherit = ariaAttributes;
  if (ignoreList && ignoreList.length > 0) {
    attributesToInherit = attributesToInherit.filter((attr) => !ignoreList.includes(attr));
  }
  return inheritAttributes(el, attributesToInherit);
};

export const ariaDescribedby = (help: string, error: string, helpElementId: string) => {
  return (help || error ) ? {'aria-describedby' : helpElementId} : null;
}